import React, { useMemo } from 'react';
import styled from 'styled-components';
import { KpiStrip } from '../../components/KpiStrip';
import { DataTable } from '../../components/DataTable';
import { Button } from '../../components/Button';
import { PageHeader } from '../../components/PageHeader';
import { Card, CardHeader } from '../../components/Card';
import { ProgressBar } from '../../components/ProgressBar';
import { Badge } from '../../components/Badge';
import { Intermediary } from './types';
import { DEMO_INTERMEDIARIES } from './demoData';
import { useIntermediarySales } from 'apiTyped/hooks/v2.intermediary.query';
import { media } from '../../theme/tokens';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatNumber = (n: number): string => n.toLocaleString('en-US');

const gradeColor = (percent: number): string => {
  if (percent >= 75) return 'var(--olive)';
  if (percent >= 50) return 'var(--amber)';
  return 'var(--crimson)';
};

// ─── Main Component ─────────────────────────────────────────────────────────

export const IntermediarySales: React.FC = () => {
  const { isLoading, intermediaries: apiData } = useIntermediarySales();

  // Use API data when available, demo data as fallback
  const data = apiData.length > 0 ? apiData : DEMO_INTERMEDIARIES;

  const kpis = useMemo(() => {
    const totalBottles = data.reduce((s, d) => s + d.bottlesSold, 0);
    const uniqueMarkets = new Set(data.flatMap((d) => d.markets.split(', ')))
      .size;
    const topMover = data.reduce(
      (best, d) => (d.yoyPercent > best.yoyPercent ? d : best),
      data[0]
    );

    return [
      {
        label: 'Active intermediaries',
        value: String(data.length),
        delta: { value: '+4 vs last quarter', direction: 'up' as const }
      },
      {
        label: 'Total bottles · Q1 2026',
        value: `${Math.round(totalBottles / 1000)}K`,
        delta: { value: '+12.4% YoY', direction: 'up' as const }
      },
      {
        label: 'Destination countries',
        value: String(uniqueMarkets),
        delta: {
          value: '+3 new this year (JP, KR, BR)',
          direction: 'up' as const
        }
      },
      {
        label: 'Top mover',
        value: topMover.name,
        delta: { value: '+6 on-trade clients · UK', direction: 'up' as const }
      }
    ];
  }, [data]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Intermediary',
        sortable: true,
        width: '22%',
        render: (row: Intermediary) => (
          <NameCell>
            <Flag className={`flag-${row.flagCode}`} />
            <NameText>
              <span>{row.name}</span>
              <Sub>{row.subtitle}</Sub>
            </NameText>
          </NameCell>
        )
      },
      {
        key: 'type',
        header: 'Type',
        width: '10%',
        render: (row: Intermediary) => <span>{row.type}</span>
      },
      {
        key: 'markets',
        header: 'Markets',
        width: '14%',
        render: (row: Intermediary) => <span>{row.markets}</span>
      },
      {
        key: 'onTradeClients',
        header: 'On-trade clients',
        sortable: true,
        width: '10%',
        render: (row: Intermediary) => <Mono>{row.onTradeClients}</Mono>
      },
      {
        key: 'bottlesSold',
        header: 'Bottles sold',
        sortable: true,
        width: '12%',
        render: (row: Intermediary) => (
          <Mono>{formatNumber(row.bottlesSold)}</Mono>
        )
      },
      {
        key: 'yoyPercent',
        header: 'YoY',
        sortable: true,
        width: '8%',
        render: (row: Intermediary) => (
          <YoY
            $positive={row.yoyPercent >= 0}
            $strong={Math.abs(row.yoyPercent) >= 15}
          >
            {row.yoyPercent >= 0 ? '+' : '−'}
            {Math.abs(row.yoyPercent)}%
          </YoY>
        )
      },
      {
        key: 'performance',
        header: 'Performance',
        sortable: true,
        width: '16%',
        render: (row: Intermediary) => (
          <PerfCell>
            <ProgressBar
              percent={row.performancePercent}
              height={6}
              showValue
            />
            <Grade
              $positive={row.performancePercent >= 65}
              $strong={row.performancePercent >= 80}
            >
              {row.performanceGrade}
            </Grade>
          </PerfCell>
        )
      },
      {
        key: 'chevron',
        header: '',
        width: '3%',
        render: () => <Chevron>›</Chevron>
      }
    ],
    []
  );

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Intermediary Sales"
        titleExtra={
          <Badge variant="default" dot={false}>
            Q4
          </Badge>
        }
        subtitle="What are your négociants and importers actually selling? Identify the countries where your wines are sold, see your importers' actual clients, and analyze every intermediary's performance."
      >
        <Button icon={<ExportIcon />}>Export Q1 report</Button>
      </PageHeader>

      {/* KPIs */}
      <KpiStrip items={kpis} />

      {/* Table */}
      <Card>
        <CardHeader
          title="Your intermediary panel"
          subtitle={`${data.length} importers & négociants tracked · click any row to drill into client list, country destinations, and Q1 performance`}
        />
        <DataTable columns={columns} data={data} />
      </Card>
    </Page>
  );
};

// ─── Icons ──────────────────────────────────────────────────────────────────

const ExportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// ─── Styled Components ──────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1400px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Flag = styled.span`
  width: 20px;
  height: 14px;
  border-radius: 2px;
  background: var(--line);
  flex-shrink: 0;
`;

const NameText = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: 500;
  font-size: 13px;
`;

const Sub = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: var(--ink-faint);
  margin-top: 2px;
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const YoY = styled.span<{ $positive: boolean; $strong: boolean }>`
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
  color: ${({ $positive }) => ($positive ? 'var(--olive)' : 'var(--crimson)')};
`;

const PerfCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Grade = styled.span<{ $positive: boolean; $strong: boolean }>`
  font-size: 12px;
  font-weight: ${({ $strong }) => ($strong ? 600 : 500)};
  color: ${({ $positive }) =>
    $positive ? 'var(--olive)' : 'var(--ink-muted)'};
  min-width: 22px;
`;

const Chevron = styled.span`
  font-size: 18px;
  color: var(--ink-faint);
`;
